import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
  Box, Heading, Text, Flex, HStack, Button, Select, SimpleGrid,
  Table, Thead, Tbody, Tr, Th, Td, TableContainer,
} from '@chakra-ui/react';
import { useParams, useNavigate } from 'react-router-dom';
import LoadingState from '../components/ui/LoadingState';
import ErrorState from '../components/ui/ErrorState';
import EmptyState from '../components/ui/EmptyState';
import Page from '../components/ui/Page';
import SectionCard from '../components/ui/SectionCard';
import Breadcrumbs from '../components/ui/Breadcrumbs';
import StatusBadge from '../components/ui/StatusBadge';
import TrendChart from '../components/SampleBarChart';
import client from '../utils/fetchClient';
import { useProjectContext } from '../context/ProjectContext';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const PAGE_SIZE = 20;

const fmtDuration = (ms) => {
  if (!ms || Number.isNaN(Number(ms))) return '—';
  const s = Math.round(Number(ms) / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
};

const fmtWhen = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  const diff = Date.now() - d.getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 60) return `${Math.max(mins, 1)} min ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
  const days = Math.round(hrs / 24);
  if (days < 7) return `${days} day${days > 1 ? 's' : ''} ago`;
  return d.toLocaleDateString();
};

const runKind = (r) => ((r.failedTests || 0) > 0 || r.status === 'failed' ? 'failed' : 'passed');

export default function GroupHistoryPage() {
  const { group } = useParams();
  const navigate = useNavigate();
  const { projects, selectedProject, setSelectedProject, effectiveProjectParam } = useProjectContext();

  const [runs, setRuns] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    setError(null);
    const query = { page, pageSize: PAGE_SIZE };
    if (effectiveProjectParam) query.project = effectiveProjectParam;
    try {
      const res = await client.get(`${API_BASE_URL}/api/test-groups/${encodeURIComponent(group)}/history`, { query });
      if (!res.ok) throw new Error('Failed to fetch group history');
      const data = await res.json();
      setRuns(Array.isArray(data?.runs) ? data.runs : []);
      setTotal(Number(data?.total) || 0);
    } catch (err) {
      setError(err.message || 'Failed to fetch group history');
      setRuns([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [group, page, effectiveProjectParam]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);
  useEffect(() => { setPage(1); }, [group, effectiveProjectParam]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Oldest -> newest for the trend lines.
  const trendData = useMemo(() => {
    return [...runs]
      .sort((a, b) => new Date(a.startTime) - new Date(b.startTime))
      .map((r) => {
        const cases = (r.passedTests || 0) + (r.failedTests || 0) + (r.skippedTests || 0);
        return {
          period: new Date(r.startTime).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
          passRate: cases ? Math.round((100 * (r.passedTests || 0)) / cases) : 0,
          duration: Math.round((r.totalDuration || 0) / 1000),
        };
      });
  }, [runs]);

  const breadcrumbs = (
    <Breadcrumbs
      items={[
        { label: 'Metrics', to: '/metrics' },
        { label: 'Test groups', to: '/test-case-groups' },
        { label: group, to: `/test-cases/${encodeURIComponent(group)}` },
        { label: 'History' },
      ]}
    />
  );

  return (
    <Page maxW="6xl">
      {breadcrumbs}
      <Flex align="flex-start" justify="space-between" wrap="wrap" gap={3} mb={5}>
        <Box>
          <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900">History — {group}</Heading>
          <Text fontSize="sm" color="gray.500">Pass rate and duration across runs that exercised this group</Text>
        </Box>
        <Select
          display={{ base: 'block', md: 'none' }}
          value={selectedProject || 'ALL PROJECTS'}
          onChange={(e) => setSelectedProject(e.target.value)}
          size="sm"
          borderRadius="full"
          maxW="200px"
          bg="white"
        >
          <option value="ALL PROJECTS">All Projects</option>
          {projects.map((p, idx) => (
            <option key={(p.id ?? p) + '-' + idx} value={p.id ?? p}>{p.name ?? p}</option>
          ))}
        </Select>
      </Flex>

      {loading ? (
        <LoadingState title="Loading group history" minH="50vh" />
      ) : error ? (
        <ErrorState message={error} onRetry={fetchHistory} />
      ) : runs.length === 0 ? (
        <SectionCard>
          <EmptyState
            emoji="🕰️"
            title="No runs for this group yet"
            subtitle="Once a run exercises this group, its history shows up here."
          />
        </SectionCard>
      ) : (
        <>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={5}>
            <SectionCard>
              <Text fontSize="sm" fontWeight="700" color="gray.900" mb={2}>Pass rate %</Text>
              <TrendChart data={trendData} chartType="line" height={220} showLegend={false}
                series={[{ key: 'passRate', label: 'Pass rate', type: 'line', color: 'green.500' }]} />
            </SectionCard>
            <SectionCard>
              <Text fontSize="sm" fontWeight="700" color="gray.900" mb={2}>Duration (s)</Text>
              <TrendChart data={trendData} chartType="line" height={220} showLegend={false}
                series={[{ key: 'duration', label: 'Duration', type: 'line', color: 'blue.500' }]} />
            </SectionCard>
          </SimpleGrid>

          <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="16px" overflow="hidden">
            <TableContainer>
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>When</Th>
                    <Th>Result</Th>
                    <Th>Passed / failed</Th>
                    <Th>Skipped</Th>
                    <Th>Duration</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  {runs.map((r) => {
                    const cases = (r.passedTests || 0) + (r.failedTests || 0) + (r.skippedTests || 0) || 1;
                    const pct = (n) => `${((100 * (n || 0)) / cases).toFixed(1)}%`;
                    return (
                      <Tr key={r.id} _hover={{ bg: 'gray.50', cursor: 'pointer' }} onClick={() => navigate(`/test-runs/${r.id}`)}>
                        <Td whiteSpace="nowrap" color="gray.600">{fmtWhen(r.startTime)}</Td>
                        <Td><StatusBadge status={runKind(r)} /></Td>
                        <Td>
                          <Flex h="7px" w="120px" borderRadius="full" overflow="hidden" bg="gray.100" mb={1}>
                            <Box w={pct(r.passedTests)} bg="status.pass" />
                            <Box w={pct(r.failedTests)} bg="status.fail" />
                            <Box w={pct(r.skippedTests)} bg="status.skip" />
                          </Flex>
                          <Text fontSize="11px" color="gray.500">{r.passedTests || 0} / {r.failedTests || 0}</Text>
                        </Td>
                        <Td fontFamily="mono" fontSize="12px">{r.skippedTests || 0}</Td>
                        <Td fontFamily="mono" fontSize="12px">{fmtDuration(r.totalDuration)}</Td>
                        <Td onClick={(e) => e.stopPropagation()}>
                          <Button size="xs" variant="outline" borderRadius="full" onClick={() => navigate(`/test-runs/${r.id}`)}>
                            Open
                          </Button>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>

          {totalPages > 1 && (
            <Flex mt={6} align="center" justify="center" gap={4}>
              <Button size="sm" variant="outline" borderRadius="full" onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page === 1}>
                Previous
              </Button>
              <Text fontSize="sm" color="gray.500">Page {page} of {totalPages}</Text>
              <Button size="sm" borderRadius="full" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} isDisabled={page >= totalPages}>
                Next
              </Button>
            </Flex>
          )}

          <HStack mt={4} spacing={2}>
            <Button variant="link" size="sm" colorScheme="gray" onClick={() => navigate(`/test-cases/${encodeURIComponent(group)}`)}>
              ← Back to {group} cases
            </Button>
          </HStack>
        </>
      )}
    </Page>
  );
}
